import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { mergeRefs } from '../lib/mergeRefs';
import { cnImage } from './Image.cn';
import { ANIMATION_DELAY_MS, emptyImage } from './Image.const';
import type { IImageProps } from './Image.types';

import './Image.css';

export const Image: React.FC<IImageProps> = (props) => {
    const {
        src,
        src2x,
        srcSet,
        sizes,
        fallbackSrc,
        className,
        imageClassName,
        stub,
        alt,
        ariaLabel,
        onClick,
        loading = 'lazy',
        width,
        height,
        borderRadius,
        innerRef,
    } = props;

    const [isLoaded, setLoaded] = useState(false);
    const [isFailed, setFailed] = useState(false);
    const [needAnimation, setNeedAnimation] = useState(false);
    const [canRemoveStub, setCanRemoveStub] = useState(false);

    // у изображения должен быть реф, и он берется либо из innerRef или из imageRef
    // важный момент: при использовании innerRef, в нем должна быть актуальная ссылка на ref
    const imageRef = useRef<HTMLImageElement>(null);

    // объединяем рефы
    const mergedRefs = useMemo(
        () => mergeRefs<HTMLImageElement>(imageRef, innerRef),
        [imageRef, innerRef],
    );

    const imageSrc = src || emptyImage;
    const legacySrcSet = src2x ? `${src2x} 2x` : undefined;
    const imageSrcSet = srcSet || legacySrcSet;

    const isRendered = useCallback(() => {
        if (!imageRef.current) {
            return false;
        }
        const { naturalWidth, naturalHeight } = imageRef.current;

        return naturalWidth > 0 && naturalHeight > 0;
    }, [imageRef]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!isLoaded && !isRendered()) {
                setNeedAnimation(true);
            }
        }, ANIMATION_DELAY_MS);

        return () => clearTimeout(timer);
    }, [isLoaded, isRendered]);

    useEffect(() => {
        if (!isLoaded && imageRef.current && imageRef.current.complete) {
            setLoaded(true);
        }
    }, [isLoaded]);

    const handleLoad = useCallback(() => {
        setLoaded(true);
    }, []);

    const handleError = useCallback(() => {
        setFailed(true);
    }, []);
    const containerClassNames = cnImage('Container', [className]);
    const imageClassNames = cnImage(
        {
            loaded: isLoaded,
            loading: needAnimation,
            animated: needAnimation && isLoaded,
        },
        // Если нет обертки, то добавляем className в изображение
        [!stub ? className : undefined, imageClassName],
    );

    const handleOnAnimationEnd = useCallback(() => {
        setCanRemoveStub(true);
    }, []);

    const sizeAttrs: Pick<IImageProps, 'width' | 'height' | 'borderRadius'> = {};
    // безопасно устанавливаем стили, чтобы избежать undefined в html
    if (width !== undefined) {
        sizeAttrs.width = width;
    }
    if (height !== undefined) {
        sizeAttrs.height = height;
    }
    if (borderRadius !== undefined) {
        sizeAttrs.borderRadius = borderRadius;
    }

    const image = (
        <img
            style={sizeAttrs}
            alt={alt}
            aria-hidden={!ariaLabel}
            aria-label={ariaLabel}
            className={imageClassNames}
            onAnimationEnd={handleOnAnimationEnd}
            onClick={onClick}
            onError={handleError}
            onLoad={handleLoad}
            ref={mergedRefs}
            src={isFailed && fallbackSrc ? fallbackSrc : imageSrc}
            srcSet={imageSrcSet}
            sizes={sizes}
            loading={loading}
        />
    );

    if (stub) {
        return (
            <div className={containerClassNames} style={sizeAttrs}>
                {needAnimation && !canRemoveStub && stub}
                {image}
            </div>
        );
    }

    return image;
};

Image.displayName = cnImage();
